'use client';

/**
 * The guest's collection code, wherever they go looking for it.
 *
 * The code was minted at checkout and shown exactly once, inside the "order
 * placed" dialog. Dismissing that dialog lost it: nothing else on any customer
 * surface read `metadata.pickup_claim_code`, so a guest arriving to collect had
 * no way to produce the code the counter was about to ask for. This is the one
 * card all three surfaces render — the orders sheet, the inline tracker and the
 * full status page — so they cannot drift into showing it in different words,
 * or in only some of the places a guest actually looks.
 *
 * Renders nothing without a code, so a dine-in or delivery order is unaffected.
 */

import { useState } from 'react';
import { Check, Copy, KeyRound, ShieldCheck } from 'lucide-react';
import { QRCode } from 'react-qrcode-logo';

import { cn } from '@/components/ui/utils';

/**
 * W-1 (pos-mobile Session 5 §1.2, owner-approved 2026-10-02): the same code as a QR the till's camera
 * can read. `menuraq:pickup:<orderId>:<code>` — the order id is the guest's own order, so nothing new is
 * exposed; the native POS parses exactly this (src/features/scan/codes.ts) and calls
 * verify_pickup_claim with it. Change the format in both places or neither.
 */
export function pickupClaimPayload(orderId: string, code: string): string {
  return `menuraq:pickup:${orderId}:${code}`;
}

export function PickupClaimCode({
  claim,
  orderId,
  className,
}: {
  claim: { code: string; status: string | null } | null | undefined;
  /** The guest's own order — when given, the code is also shown as a scannable QR. */
  orderId?: string | null;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  if (!claim?.code) return null;

  const verified = claim.status === 'verified';

  async function copy() {
    if (!claim?.code) return;
    try {
      await navigator.clipboard.writeText(claim.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* private / insecure context — the code is on screen either way */
    }
  }

  return (
    <div
      className={cn(
        'rounded-2xl border p-4 text-center',
        verified
          ? 'border-[var(--color-success)]/30 bg-[var(--color-success-soft)]'
          : 'border-[var(--pub-border-subtle)] bg-card',
        className,
      )}
    >
      <p className="flex items-center justify-center gap-1.5 text-2xs font-semibold uppercase tracking-widest text-[var(--color-text-secondary)]">
        {verified ? (
          <ShieldCheck aria-hidden="true" className="size-3.5 text-[var(--color-success)]" />
        ) : (
          <KeyRound aria-hidden="true" className="size-3.5" />
        )}
        {verified ? 'Code checked' : 'Collection code'}
      </p>
      <p className="mt-1 text-[26px] font-bold tracking-widest text-[var(--color-text-primary)]">
        {claim.code}
      </p>
      {orderId && !verified ? (
        // Always dark-on-white with a quiet zone: a QR must scan whatever the page theme.
        <div className="mx-auto mt-2 w-fit rounded-xl bg-white p-2" aria-label="Collection code as a QR for the counter to scan">
          <QRCode value={pickupClaimPayload(orderId, claim.code)} size={132} quietZone={8} bgColor="#FFFFFF" fgColor="#0F382C" ecLevel="M" />
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => void copy()}
        className="mt-1 inline-flex min-h-9 items-center justify-center gap-1 rounded-lg px-2 text-2xs text-[var(--color-text-secondary)] underline-offset-4 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copied ? <Check aria-hidden="true" className="size-3" /> : <Copy aria-hidden="true" className="size-3" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <p className="text-2xs text-[var(--color-text-secondary)]">
        {verified ? 'Already checked at the counter.' : 'Show this when you collect.'}
      </p>
    </div>
  );
}
